import { useCallback, useEffect, useRef, useState } from 'react';
import gql from 'graphql-tag';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { cfetch } from '@confluence/network';

import type {
	RawAction,
	RawSuggestion,
	SpaceSuggestionsResponse,
} from '../__types__/SpaceSuggestion.ts';

export type UseFetchSpaceSuggestionsResult = {
	suggestions: RawSuggestion[];
	isLoading: boolean;
	error: Error | undefined;
	refetch: () => Promise<void>;
};

const buildSpaceSuggestionsUrl = (spaceKey: string): string =>
	`/wiki/rest/internal/1.0/spaces/${spaceKey}/organization/suggestions`;

type ContentTitleNode = {
	id: string | null | undefined;
	title: string | null | undefined;
};

type ContentTitleConnection = {
	nodes?: readonly (ContentTitleNode | null)[] | null;
} | null;

type ContentTitlesQueryData = {
	pageContent?: ContentTitleConnection;
	folderContent?: ContentTitleConnection;
	whiteboardContent?: ContentTitleConnection;
	databaseContent?: ContentTitleConnection;
	embedContent?: ContentTitleConnection;
	blogpostContent?: ContentTitleConnection;
	slideContent?: ContentTitleConnection;
};

type ContentTitlesQueryVariables = {
	ids: string[];
};

const ContentTitlesQuery = gql`
	query useFetchSpaceSuggestionsContentTitlesQuery($ids: [ID!]!) {
		pageContent: content(ids: $ids, type: "page") {
			nodes {
				id
				title
			}
		}
		folderContent: content(ids: $ids, type: "folder") {
			nodes {
				id
				title
			}
		}
		whiteboardContent: content(ids: $ids, type: "whiteboard") {
			nodes {
				id
				title
			}
		}
		databaseContent: content(ids: $ids, type: "database") {
			nodes {
				id
				title
			}
		}
		embedContent: content(ids: $ids, type: "embed") {
			nodes {
				id
				title
			}
		}
		blogpostContent: content(ids: $ids, type: "blogpost") {
			nodes {
				id
				title
			}
		}
		slideContent: content(ids: $ids, type: "slide") {
			nodes {
				id
				title
			}
		}
	}
`;

/**
 * Extracts the content ID referenced by a single action, or `undefined` if
 * the action type does not reference content.
 */
function getContentIdFromAction(action: RawAction): string | undefined {
	switch (action.actionType) {
		case 'MOVE':
		case 'RENAME':
			return action.parameters.contentId;
		case 'CREATE_FOLDER':
			return action.parameters.parentContentId;
		default:
			return undefined;
	}
}

/**
 * Extracts every content ID referenced by the actions in a set of suggestions,
 * skipping suggestions that already carry `titleReferences` (those names are
 * resolved locally without a network call).
 * Returns a deduplicated array of IDs.
 */
function* iterateSuggestionActions(
	suggestions: RawSuggestion[],
): Generator<RawSuggestion['actions'][number]> {
	for (const suggestion of suggestions) {
		if (!suggestion.titleReferences) {
			yield* suggestion.actions;
		}
	}
}

function collectContentIds(suggestions: RawSuggestion[]): string[] {
	const ids = new Set<string>();
	for (const action of iterateSuggestionActions(suggestions)) {
		const id = getContentIdFromAction(action);
		if (id !== undefined) {
			ids.add(id);
		}
	}
	return Array.from(ids);
}

/**
 * Batch-fetches content titles for the given IDs using Confluence AGG content
 * lookup. Returns a map from content ID to its title.
 *
 * IDs that cannot be resolved (e.g. deleted content, aliases, content types
 * outside the queried set) are silently omitted from the returned map —
 * callers should fall back to the raw ID.
 */
async function fetchContentNames(contentIds: string[]): Promise<Record<string, string>> {
	if (contentIds.length === 0) {
		return {};
	}

	try {
		const { data, error } = await queryInterop<ContentTitlesQueryData, ContentTitlesQueryVariables>(
			{
				query: ContentTitlesQuery as unknown as Parameters<typeof queryInterop>[0]['query'],
				variables: {
					ids: contentIds,
				},
				fetchPolicy: 'network-only',
				interopApolloAggClient: true,
			},
		);

		if (error) {
			return {};
		}

		const contentTitleNodes = [
			data?.pageContent?.nodes,
			data?.folderContent?.nodes,
			data?.whiteboardContent?.nodes,
			data?.databaseContent?.nodes,
			data?.embedContent?.nodes,
			data?.blogpostContent?.nodes,
			data?.slideContent?.nodes,
		].flatMap((nodes) => nodes ?? []);

		const names: Record<string, string> = {};
		for (const node of contentTitleNodes) {
			if (node?.id && node.title) {
				names[node.id] = node.title;
			}
		}
		return names;
	} catch {
		// Content title resolution is best-effort; fall back to raw IDs on failure.
		return {};
	}
}

/**
 * Builds a map from CREATE_FOLDER alias → folder title within a single
 * suggestion. Used to resolve `destinationAlias` in MOVE actions to a
 * human-readable folder name.
 */
function buildAliasToTitleMap(actions: RawAction[]): Record<string, string> {
	const map: Record<string, string> = {};
	for (const action of actions) {
		if (action.actionType === 'CREATE_FOLDER') {
			map[action.parameters.alias] = action.parameters.title;
		}
	}
	return map;
}

/**
 * Returns a copy of the action with any referenced content ID replaced by
 * its resolved human-readable name from `names`, and any destination alias
 * replaced by the folder title from `aliasToTitle`. Falls back to the raw
 * ID or alias when a name is not available.
 */
function enrichActionWithNames(
	action: RawAction,
	names: Record<string, string>,
	aliasToTitle: Record<string, string>,
): RawAction {
	switch (action.actionType) {
		case 'MOVE':
			return {
				...action,
				parameters: {
					...action.parameters,
					contentName: names[action.parameters.contentId] ?? action.parameters.contentId,
					destinationName:
						aliasToTitle[action.parameters.destinationAlias] ?? action.parameters.destinationAlias,
				},
			};
		case 'RENAME':
			return {
				...action,
				parameters: {
					...action.parameters,
					contentName: names[action.parameters.contentId] ?? action.parameters.contentId,
				},
			};
		case 'CREATE_FOLDER':
			return {
				...action,
				parameters: {
					...action.parameters,
					parentContentName:
						names[action.parameters.parentContentId] ?? action.parameters.parentContentId,
				},
			};
		default:
			return action;
	}
}

/**
 * Enriches raw suggestion actions with resolved content names and alias titles.
 * Falls back to the raw content ID or alias when a name is not available.
 */
function enrichActionsWithNames(actions: RawAction[], names: Record<string, string>): RawAction[] {
	const aliasToTitle = buildAliasToTitleMap(actions);
	return actions.map((action) => enrichActionWithNames(action, names, aliasToTitle));
}

/**
 * Builds a per-suggestion names map from `titleReferences` in a single linear
 * pass, keyed by suggestion ID. Suggestions without `titleReferences` are
 * omitted and fall back to the globally-fetched AGG names map.
 */
function buildNamesBySuggestionId(
	suggestions: RawSuggestion[],
): Record<string, Record<string, string>> {
	const result: Record<string, Record<string, string>> = {};
	for (const suggestion of suggestions) {
		if (!suggestion.titleReferences) {
			continue;
		}
		const names: Record<string, string> = {};
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const id in suggestion.titleReferences) {
			const ref = suggestion.titleReferences[id];
			names[ref.contentId] = ref.title;
		}
		result[suggestion.id] = names;
	}
	return result;
}

function enrichSuggestionsWithNames(
	suggestions: RawSuggestion[],
	names: Record<string, string>,
): RawSuggestion[] {
	// Pre-build per-suggestion name maps from titleReferences in one linear pass
	// to avoid nested iteration inside the map below.
	const namesBySuggestionId = buildNamesBySuggestionId(suggestions);
	return suggestions.map((suggestion) => {
		// Prefer names sourced directly from the API response via titleReferences.
		// Fall back to the globally-fetched AGG names for suggestions without it.
		const resolvedNames = namesBySuggestionId[suggestion.id] ?? names;
		return {
			...suggestion,
			actions: enrichActionsWithNames(suggestion.actions, resolvedNames),
		};
	});
}

/**
 * Fetches the Space Organizer Inbox suggestions for the given space.
 *
 * Note: the backend endpoint is currently a mock and returns hardcoded data
 * regardless of the `spaceKey` provided. Migrate to a GraphQL query (via
 * `@confluence/apollo-relay-interop`) once the AGG schema is ready — REST is
 * deprecated for new Confluence frontend data fetching.
 *
 * After fetching suggestions, content IDs referenced by actions are
 * batch-resolved to human-readable titles via AGG content lookup.
 *
 * Concurrency: tracks an epoch per call so that a stale response from a
 * previous `spaceKey` cannot overwrite state for the current `spaceKey`.
 */
export const useFetchSpaceSuggestions = (spaceKey: string): UseFetchSpaceSuggestionsResult => {
	const [suggestions, setSuggestions] = useState<RawSuggestion[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const callEpochRef = useRef<number>(0);

	const refetch = useCallback(async () => {
		const epoch = callEpochRef.current + 1;
		callEpochRef.current = epoch;

		setIsLoading(true);
		setError(undefined);

		try {
			const response = await cfetch(buildSpaceSuggestionsUrl(spaceKey), {
				method: 'GET',
			});

			if (!response.ok) {
				throw new Error(
					`Failed to fetch space suggestions: ${response.status} ${response.statusText}`,
				);
			}

			const data: SpaceSuggestionsResponse = await response.json();

			// Discard the response if a newer call has been issued in the meantime.
			if (callEpochRef.current !== epoch) {
				return;
			}

			const rawSuggestions = data.suggestions ?? [];

			// Resolve content IDs to human-readable titles (best-effort).
			const contentIds = collectContentIds(rawSuggestions);
			const contentNames = await fetchContentNames(contentIds);

			// Re-check epoch after the async name resolution.
			if (callEpochRef.current !== epoch) {
				return;
			}

			setSuggestions(enrichSuggestionsWithNames(rawSuggestions, contentNames));
		} catch (err) {
			if (callEpochRef.current !== epoch) {
				return;
			}
			setError(err instanceof Error ? err : new Error(String(err)));
			setSuggestions([]);
		} finally {
			if (callEpochRef.current === epoch) {
				setIsLoading(false);
			}
		}
	}, [spaceKey]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void refetch();
	}, [refetch]);

	return { suggestions, isLoading, error, refetch };
};
